import React from "react";
import { QuizResult } from "../../shared/types"; // Add this import
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { GetApp, PictureAsPdf } from "@mui/icons-material";
// Import the Recommendation type
import { Recommendation, ChartTooltipContext } from "../../types/chart-types";

interface CloudPDFExportProps {
  results: QuizResult[];
  recommendations: Recommendation[]; // Use the proper type instead of any[]
  resultsRef: React.RefObject<HTMLDivElement>;
}

const CloudPDFExport: React.FC<CloudPDFExportProps> = ({
  results,
  recommendations,
  resultsRef,
}) => {
  const exportToPDF = async () => {
    const currentYear = new Date().getFullYear();
    try {
      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = "⏳ Generating PDF...";
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (currentY: number, neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - margin) {
          pdf.addPage();
          return margin;
        }
        return currentY;
      };

      // Helper function to add footer with branding to each page
      const addFooter = () => {
        // Footer background with solid color
        pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to match CRM color
        pdf.rect(0, pageHeight - 25, pageWidth, 25, "F");

        // Footer text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(
          `© ${currentYear} Inthera Consulting Group. All rights reserved.`,
          pageWidth / 2,
          pageHeight - 15,
          { align: "center" }
        );

        // Add clickable link for website
        pdf.text(
          "Visit us at: intheraconsultinggroup.com",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" }
        );

        // Add clickable link
        pdf.link(pageWidth / 2 - 25, pageHeight - 12, 50, 8, {
          url: "https://intheraconsultinggroup.com/",
        });
      };

      // Load the logo image
      const loadLogoImage = (): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;

            if (ctx) {
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } else {
              reject("Failed to get canvas context");
            }
          };
          img.onerror = () => reject("Failed to load logo");
          img.src = "/assets/inthera-logo.png"; // Adjust path as needed
        });
      };

      let logoDataUrl: string | null = null;
      try {
        logoDataUrl = await loadLogoImage();
      } catch (error) {
        console.warn("Could not load logo, continuing without it:", error);
      }

      // Title Page with Enhanced Header - Update to CRM color
      pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to match CRM color
      pdf.rect(0, 0, pageWidth, 70, "F");

      // Add Inthera logo if available
      if (logoDataUrl) {
        const logoWidth = 40;
        const logoHeight = 25;
        const logoX = pageWidth / 2 - logoWidth / 2;
        const logoY = 15;

        pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);

        // Add clickable area for the logo
        pdf.link(logoX, logoY, logoWidth, logoHeight, {
          url: "https://intheraconsultinggroup.com/",
        });
      }

      // Main title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.text("Cloud Solutions Roadmap", pageWidth / 2, 50, {
        align: "center",
      });

      pdf.setFontSize(12);
      pdf.text(
        `Generated on ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        60,
        { align: "center" }
      );

      let yPosition = 90;

      // Executive Summary
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(18);
      pdf.text("Executive Summary", margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(11);
      const summaryText = `This report provides personalized cloud infrastructure recommendations based on your business requirements. We analyzed ${
        results.length
      } survey responses to identify ${
        recommendations.length
      } key cloud solutions across ${
        [...new Set(recommendations.map((r) => r.category))].length
      } business areas that will maximize your cloud investment and improve operational efficiency.`;
      const summaryLines = pdf.splitTextToSize(summaryText, contentWidth);
      pdf.text(summaryLines, margin, yPosition);
      yPosition += summaryLines.length * 5 + 15;

      // Key Statistics and Analytics
      yPosition = checkPageBreak(yPosition, 50);
      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Key Statistics & Analytics", margin, yPosition);
      yPosition += 12;

      const highPriority = recommendations.filter(
        (r) => r.priority === "High"
      ).length;
      const mediumPriority = recommendations.filter(
        (r) => r.priority === "Medium"
      ).length;
      const lowPriority = recommendations.filter(
        (r) => r.priority === "Low"
      ).length;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      // Statistics with better formatting
      pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Total Recommended Solutions: ${recommendations.length}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(239, 68, 68); // Red for high priority
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `High Priority Solutions: ${highPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(245, 158, 11); // Orange for medium priority
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Medium Priority Solutions: ${mediumPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(16, 185, 129); // Green for low priority
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(`Low Priority Solutions: ${lowPriority}`, margin + 8, yPosition);
      yPosition += 15;

      // Analytics breakdown by category
      const categoryBreakdown = recommendations.reduce((acc: any, rec: any) => {
        acc[rec.category] = (acc[rec.category] || 0) + 1;
        return acc;
      }, {});

      pdf.setFontSize(12);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Solutions by Business Area:", margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      Object.entries(categoryBreakdown).forEach(([category, count]) => {
        pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
        pdf.circle(margin + 2, yPosition - 1.5, 0.8, "F");
        pdf.text(`${category}: ${count} solutions`, margin + 8, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Average recommendation score and estimated costs
      const avgScore =
        recommendations.reduce((sum: number, rec: any) => sum + rec.score, 0) /
        recommendations.length;

      pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Average Solution Score: ${avgScore.toFixed(1)}/100`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      // Estimated cost information
      pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Estimated Monthly Cost Range: $50-$1,000+ (varies by solution)`,
        margin + 8,
        yPosition
      );
      yPosition += 15;

      // Add footer to first page
      addFooter();

      // Capture Charts Section
      yPosition = checkPageBreak(yPosition, 120);
      if (yPosition === margin) {
        addFooter();
      }

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Visual Analytics Dashboard", margin, yPosition);
      yPosition += 15;

      // Chart capture logic similar to CRMPDFExport
      console.log("=== Starting chart capture process ===");

      let chartsAdded = 0;
      let chartY = yPosition;
      let chartX = margin;
      const chartsPerRow = 2;
      const chartWidth = (contentWidth - 15) / chartsPerRow;
      const chartHeight = 80;

      try {
        // Step 1: Check current charts
        let chartElements = document.querySelectorAll("canvas");
        console.log(`Initial canvas count: ${chartElements.length}`);

        // Step 2: If no charts found, try to find and click analytics button
        if (chartElements.length === 0) {
          console.log("No charts found, looking for analytics button...");

          // Try multiple ways to find the analytics button
          const possibleButtons = [
            ...Array.from(document.querySelectorAll("button")),
            ...Array.from(document.querySelectorAll('[role="tab"]')),
            ...Array.from(document.querySelectorAll("a")),
            ...Array.from(document.querySelectorAll('[class*="tab"]')),
          ];

          const analyticsButton = possibleButtons.find((button) => {
            const text = button.textContent?.toLowerCase() || "";
            return (
              text.includes("analytics") ||
              text.includes("chart") ||
              text.includes("visual") ||
              text.includes("dashboard")
            );
          });

          if (analyticsButton) {
            console.log("Found analytics button, clicking...");
            (analyticsButton as HTMLElement).click();

            // Wait for charts to load
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Re-query for charts
            chartElements = document.querySelectorAll("canvas");
            console.log(
              `Canvas count after tab switch: ${chartElements.length}`
            );
          } else {
            console.log("No analytics button found");
          }
        }

        // Step 3: Try to capture charts
        if (chartElements.length > 0) {
          console.log("Attempting to capture charts...");

          for (let i = 0; i < Math.min(chartElements.length, 4); i++) {
            const canvas = chartElements[i] as HTMLCanvasElement;

            // Basic validation
            if (canvas.width < 50 || canvas.height < 50) {
              console.log(`Skipping small canvas ${i}`);
              continue;
            }

            const rect = canvas.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
              console.log(`Skipping invisible canvas ${i}`);
              continue;
            }

            try {
              // Create image from canvas
              const tempCanvas = document.createElement("canvas");
              const tempCtx = tempCanvas.getContext("2d");
              tempCanvas.width = canvas.width;
              tempCanvas.height = canvas.height;

              if (tempCtx) {
                tempCtx.fillStyle = "#ffffff";
                tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                tempCtx.drawImage(canvas, 0, 0);
              }

              const chartImage = tempCanvas.toDataURL("image/png", 1.0);

              // Check if we got a valid image
              if (chartImage.length < 1000) {
                console.log(`Canvas ${i} produced empty image`);
                continue;
              }

              // Position chart on PDF
              if (chartsAdded > 0 && chartsAdded % chartsPerRow === 0) {
                chartY += chartHeight + 15;
                chartX = margin;
                const newY = checkPageBreak(chartY, chartHeight + 15);
                if (newY === margin) {
                  addFooter();
                  chartY = newY;
                } else {
                  chartY = newY;
                }
              }

              // Add chart title - cloud specific titles
              const chartTitles = [
                "ROI-Based Priority",
                "Solutions by Business Area",
                "Business Impact Score",
                "ROI vs Implementation Score",
              ];

              pdf.setFontSize(10);
              pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
              pdf.text(
                chartTitles[chartsAdded] || `Chart ${chartsAdded + 1}`,
                chartX,
                chartY - 3
              );

              // Add border
              pdf.setDrawColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
              pdf.setLineWidth(0.5);
              pdf.rect(chartX, chartY, chartWidth, chartHeight);

              // Add chart image to PDF
              pdf.addImage(
                chartImage,
                "PNG",
                chartX + 1,
                chartY + 1,
                chartWidth - 2,
                chartHeight - 2
              );

              chartX += chartWidth + 15;
              chartsAdded++;
              console.log(`Successfully added chart ${chartsAdded}`);
            } catch (canvasError) {
              console.error(`Error processing canvas ${i}:`, canvasError);
              continue;
            }
          }
        }

        // Step 4: If still no charts, try html2canvas on the entire page
        if (chartsAdded === 0 && resultsRef.current) {
          console.log("Trying to capture entire results area...");

          try {
            const entirePageCanvas = await html2canvas(resultsRef.current, {
              backgroundColor: "#ffffff",
              scale: 0.8,
              logging: false,
              useCORS: true,
              allowTaint: true,
              height: 600, // Limit height
              width: 800, // Limit width
            });

            const pageImage = entirePageCanvas.toDataURL("image/png", 0.9);

            if (pageImage.length > 1000) {
              pdf.setFontSize(10);
              pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
              pdf.text("Cloud Solutions Overview", chartX, chartY - 3);

              pdf.setDrawColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
              pdf.setLineWidth(0.5);
              pdf.rect(chartX, chartY, contentWidth, chartHeight * 1.5);

              pdf.addImage(
                pageImage,
                "PNG",
                chartX + 1,
                chartY + 1,
                contentWidth - 2,
                chartHeight * 1.5 - 2
              );

              chartsAdded = 1;
              console.log("Successfully captured page overview");
            }
          } catch (pageError) {
            console.error("Error capturing page:", pageError);
          }
        }
      } catch (error) {
        console.error("Chart capture error:", error);
      }

      // Update yPosition based on results
      if (chartsAdded > 0) {
        yPosition = chartY + chartHeight + 25;
        console.log(`Chart capture complete: ${chartsAdded} charts added`);
      } else {
        // Show user-friendly message
        pdf.setFontSize(11);
        pdf.setTextColor(75, 85, 99);
        const helpLines = [
          "Charts are not currently visible for capture.",
          "For best results:",
          "• Navigate to the Analytics tab",
          "• Wait for charts to fully load",
          "• Then export the PDF",
        ];

        helpLines.forEach((line, index) => {
          pdf.text(line, margin, yPosition + index * 6);
        });

        yPosition += helpLines.length * 6 + 10;
        console.log("No charts captured - added help text");
      }

      // Quiz Responses Section - START ON NEW PAGE
      addFooter(); // Add footer to current page
      pdf.addPage(); // Start new page
      yPosition = margin; // Reset position for new page

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Your Survey Responses", margin, yPosition);
      yPosition += 15;

      results.forEach((result, index) => {
        const responseY = checkPageBreak(yPosition, 25);
        if (responseY === margin) {
          addFooter();
          yPosition = responseY;
        } else {
          yPosition = responseY;
        }

        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(`Q${index + 1}: ${result.question}`, margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
        const answerLines = pdf.splitTextToSize(
          `Answer: ${result.answer}`,
          contentWidth - 10
        );
        pdf.text(answerLines, margin + 5, yPosition);
        yPosition += answerLines.length * 5 + 8;
      });

      // Add footer to current page before new page
      addFooter();

      // Detailed Recommendations Section
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Detailed Cloud Solution Recommendations", margin, yPosition);
      yPosition += 20;

      // Sort recommendations by priority for better presentation
      const sortedRecommendations = [...recommendations].sort((a, b) => {
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return (
          priorityOrder[b.priority as keyof typeof priorityOrder] -
          priorityOrder[a.priority as keyof typeof priorityOrder]
        );
      });

      sortedRecommendations.forEach((rec, index) => {
        const recY = checkPageBreak(yPosition, 80);
        if (recY === margin) {
          addFooter();
          yPosition = recY;
        } else {
          yPosition = recY;
        }

        // Priority badge with better styling
        const priorityColor: [number, number, number] =
          rec.priority === "High"
            ? [239, 68, 68]
            : rec.priority === "Medium"
            ? [245, 158, 11]
            : [16, 185, 129];

        pdf.setFillColor(...priorityColor);
        pdf.roundedRect(margin, yPosition - 5, 25, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(rec.priority.toUpperCase(), margin + 12.5, yPosition, {
          align: "center",
        });

        // Score badge
        pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
        pdf.roundedRect(margin + 30, yPosition - 5, 20, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${rec.score}`, margin + 40, yPosition, { align: "center" });

        // Recommendation title
        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(14);
        pdf.text(`${index + 1}. ${rec.title}`, margin + 55, yPosition);
        yPosition += 12;

        // Category and implementation info
        pdf.setFontSize(10);
        pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
        pdf.text(`Category: ${rec.category}`, margin, yPosition);
        pdf.text(`Timeline: ${rec.implementation}`, margin + 70, yPosition);
        pdf.text(`ROI: ${rec.roi}`, margin + 140, yPosition);
        yPosition += 10;

        // Description with better formatting
        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);
        const descLines = pdf.splitTextToSize(rec.description, contentWidth);
        pdf.text(descLines, margin, yPosition);
        yPosition += descLines.length * 4 + 8;

        // Benefits section with improved styling
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Key Benefits:", margin, yPosition);
        yPosition += 6;

        // Benefits background
        const benefitsHeight = rec.benefits.length * 5 + 4;
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(
          margin,
          yPosition - 2,
          contentWidth,
          benefitsHeight,
          2,
          2,
          "F"
        );

        rec.benefits.forEach((benefit: string) => {
          pdf.setFillColor(16, 185, 129);
          pdf.circle(margin + 5, yPosition + 1.5, 1, "F");
          pdf.setTextColor(75, 85, 99);
          pdf.setFontSize(9);
          const benefitLines = pdf.splitTextToSize(benefit, contentWidth - 15);
          pdf.text(benefitLines, margin + 10, yPosition + 3);
          yPosition += benefitLines.length * 4 + 1;
        });

        // Cloud-specific: Add monthly cost estimate and provider info
        yPosition += 6;
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Implementation Details:", margin, yPosition);
        yPosition += 6;

        // Details background
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, yPosition - 2, contentWidth, 14, 2, 2, "F");

        pdf.setTextColor(75, 85, 99);
        pdf.setFontSize(9);
        pdf.text(
          `Monthly Cost Estimate: ${rec.monthlyEstimate}`,
          margin + 10,
          yPosition + 3
        );
        pdf.text(
          `Recommended Provider: ${rec.provider}`,
          margin + 10,
          yPosition + 8
        );
        yPosition += 16;

        yPosition += 10; // Spacing between recommendations
      });

      // Add footer to recommendations page
      addFooter();

      // Implementation Roadmap Section
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Implementation Roadmap", margin, yPosition);
      yPosition += 20;

      // Group recommendations by implementation timeline
      const timelineGroups = recommendations.reduce((acc: any, rec: any) => {
        const timeline = rec.implementation || "Unknown";
        if (!acc[timeline]) acc[timeline] = [];
        acc[timeline].push(rec);
        return acc;
      }, {});

      Object.entries(timelineGroups).forEach(
        ([timeline, recs]: [string, any]) => {
          const timelineY = checkPageBreak(yPosition, 40);
          if (timelineY === margin) {
            addFooter();
            yPosition = timelineY;
          } else {
            yPosition = timelineY;
          }

          pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
          pdf.roundedRect(margin, yPosition - 5, contentWidth, 12, 2, 2, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(12);
          pdf.text(
            `${timeline} (${recs.length} solutions)`,
            margin + 5,
            yPosition + 2
          );
          yPosition += 18;

          recs.forEach((rec: any, index: number) => {
            pdf.setFontSize(10);
            pdf.setTextColor(31, 41, 55);
            pdf.text(`${index + 1}. ${rec.title}`, margin + 5, yPosition);
            pdf.setTextColor(75, 85, 99);
            pdf.text(`Score: ${rec.score}/100`, margin + 120, yPosition);
            pdf.text(`Priority: ${rec.priority}`, margin + 160, yPosition);
            yPosition += 6;
          });
          yPosition += 10;
        }
      );

      // Cloud specific: Cost estimation section - START ON NEW PAGE
      addFooter(); // Add footer to current page
      pdf.addPage(); // Force a new page
      yPosition = margin; // Reset position to top of new page

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Cost Optimization Insights", margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      const costInsightText =
        "The estimated monthly costs for the recommended cloud solutions range from basic subscriptions to enterprise-level implementations. Actual costs will depend on your specific usage patterns, scaling requirements, and chosen service tiers. Cost optimization strategies like reserved instances, spot instances, and right-sizing can reduce these estimates by 20-40%.";
      const costLines = pdf.splitTextToSize(costInsightText, contentWidth);
      pdf.text(costLines, margin, yPosition);
      yPosition += costLines.length * 4 + 15;

      // Cost savings tips
      pdf.setFontSize(12);
      pdf.setTextColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
      pdf.text("Cost Saving Strategies:", margin, yPosition);
      yPosition += 10;

      const costTips = [
        "Use reserved instances for predictable workloads (25-75% savings)",
        "Implement auto-scaling to match resource allocation with demand",
        "Utilize spot instances for non-critical, interruptible workloads (70-90% savings)",
        "Enable resource scheduling to shut down non-production environments after hours",
        "Implement proper tagging strategy for cost allocation and tracking",
      ];

      costTips.forEach((tip, index) => {
        pdf.setFillColor(21, 42, 83); // Changed from 103, 126, 234 to CRM color
        pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);
        const tipLines = pdf.splitTextToSize(tip, contentWidth - 10);
        pdf.text(tipLines, margin + 8, yPosition);
        yPosition += tipLines.length * 4 + 5;
      });

      // Add footer to final page
      addFooter();

      pdf.save("inthera-cloud-comprehensive-analysis-report.pdf");

      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating the PDF. Please try again.");

      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    }
  };

  return (
    <button
      data-pdf-export
      onClick={exportToPDF}
      style={{
        padding: "1rem 2rem",
        background: "rgb(201, 167, 80)",
        color: "white",
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        fontSize: "1.1rem",
        fontWeight: "600",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(201, 167, 80, 0.3)",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "1rem",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(201, 167, 80, 0.4)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 15px rgba(201, 167, 80, 0.3)";
      }}
    >
      📊 Export Comprehensive Report
    </button>
  );
};

export default CloudPDFExport;
